import { HTMLAttributes, useMemo } from 'react'
import Link from 'next/link'

import Container from '@/components/base/container'
import Typography from '@/components/base/typography'
import { pageHasHero } from '@/utils/page'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type BreadcrumbsProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    pageProps: WPPageData & {
      title?: { rendered?: string } | string
      breadcrumbs?: Array<{ title?: string; url?: string }>
    }
  }>

const stripHtml = (value?: string) => {
  if (!value) return ''
  return value.replace(/<[^>]*>/g, '').trim()
}

const stripPrefixToken = (value?: string) => {
  if (!value) return ''

  return value.replace(/^(title|url):/i, '').trim()
}

const getItemsFromPageBreadcrumbs = (
  pageProps?: BreadcrumbsProps['pageProps']
) => {
  const pageBreadcrumbs = pageProps?.breadcrumbs || []
  const pageTitle = stripPrefixToken(
    stripHtml(
      typeof pageProps?.title === 'string'
        ? pageProps.title
        : pageProps?.title?.rendered
    )
  )

  const baseItems: Array<{ label: string; href?: string }> = pageBreadcrumbs
    .slice(0, -1)
    .map((item) => ({
      label: stripPrefixToken(stripHtml(item?.title)),
      href: stripPrefixToken(item?.url)
    }))
    .filter((item) => Boolean(item.label))
    .filter((item) => item.label.toLowerCase() !== 'home')

  if (pageTitle) {
    return [...baseItems, { label: pageTitle, href: undefined }]
  }

  return baseItems
}

const Breadcrumbs = ({ pageProps, className }: BreadcrumbsProps) => {
  const resolvedItems = useMemo(() => {
    return getItemsFromPageBreadcrumbs(pageProps)
  }, [pageProps])

  if (resolvedItems.length === 0) return null
  if (!pageProps?.acf?.page?.show_breadcrumbs) return null

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const hasHero = useMemo(() => pageHasHero(pageProps), [pageProps])

  return (
    <Container
      className={cn(
        'relative z-10 flex h-[4.3rem] items-center text-text-primary-light dark:text-text-primary-dark',
        {
          'absolute top-[var(--header-height)] z-10 text-text-primary-invert-light':
            hasHero
        },
        className
      )}
      data-motion-breadcrumbs
      data-breadcrumbs
    >
      <nav aria-label="Breadcrumb">
        <ol className="flex items-end gap-spacing-2xs">
          <li aria-hidden className="flex items-end">
            <svg
              width="8"
              height="2"
              viewBox="0 0 8 2"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0 1C0 0.447715 0.358172 0 0.8 0H7.2C7.64183 0 8 0.447715 8 1C8 1.55228 7.64183 2 7.2 2H0.8C0.358172 2 0 1.55228 0 1Z"
              />
            </svg>
          </li>

          {resolvedItems.map((item, index) => {
            const isLast = index === resolvedItems.length - 1

            return (
              <li
                key={`breadcrumb-item-${item.label}-${index}`}
                className="flex items-end gap-spacing-2xs"
              >
                {index > 0 ? (
                  <svg
                    width="7"
                    height="12"
                    viewBox="0 0 7 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M0.864008 11.904H7.80821e-06L4.92801 9.53674e-07H5.74401L0.864008 11.904Z"
                      fill="black"
                    />
                  </svg>
                ) : null}

                {item.href && !isLast ? (
                  <Link href={item.href} scroll={false} className="flex">
                    <Typography
                      variant="regular"
                      component="span"
                      className="condensed ease-in-out inline-flex transition-opacity duration-300 hover:opacity-50"
                    >
                      {item.label}
                    </Typography>
                  </Link>
                ) : (
                  <Typography
                    variant="regular"
                    component="span"
                    className={cn('condensed', {
                      'opacity-50': isLast && !hasHero
                    })}
                  >
                    {item.label}
                  </Typography>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    </Container>
  )
}

export default Breadcrumbs
